import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import {
  ProductCardDataLayerContainer,
} from '../../containers/ProductCardDataLayerContainer/ProductCardDataLayerContainer';
import {
  TinyProductCardDataLayerContainer,
} from '../../containers/TinyProductCardDataLayerContainer/TinyProductCardDataLayerContainer';
import { ITEM_POSITION_SHIFT } from '../../hooks/productListHooks/productListHooks';
import { LayoutExtension } from '../../types/ResponsiveState';
import { ProductType } from '../../types/apiEnums';
import { ColorPresentational, ImagePresentational, ProductCardType } from '../../types/common';
import { ItemListID } from '../../types/dataLayer';
import { SearchHighlightedBlock } from '../Search/Search';
import { Typography } from '../Typography/Typography';
import styles from './SearchSuggestions.module.css';

const messages = defineMessages({
  title: {
    id: 'src.components.SearchSuggestions.title',
    defaultMessage: 'Popular coupons',
  },
});

const MAJOR_HIGHLIGHTED_BLOCK_IDX = 0;

export type SearchSuggestion = {
  backgroundColor: ColorPresentational;
  description: string | null;
  image: ImagePresentational;
  path: string | null;
  productType: ProductType;
  showShadow: boolean;
  title: string;
};

export type Props = {
  highlightedBlocks?: SearchHighlightedBlock[];
  layoutExtension: LayoutExtension;
  suggestions?: SearchSuggestion[];
};
export const SearchSuggestions: React.FC<Props> = React.memo(({
  suggestions,
  highlightedBlocks,
  layoutExtension,
}) => {
  const numberOfHighlightedBlocks = React.useMemo(() => (highlightedBlocks?.length ? 1 : 0), [
    highlightedBlocks?.length,
  ]);
  return (
    <div className={styles.searchSuggestions}>
      <Typography component='div' variant='body2' className={styles.title}>
        <FormattedMessage {...messages.title} />
      </Typography>

      <div className={styles.productsContainer}>
        {
          highlightedBlocks && highlightedBlocks.length ? (
            <div className={styles.highlightedBlock}>
              {
                layoutExtension === LayoutExtension.Mobile ? (
                  <TinyProductCardDataLayerContainer
                    position={MAJOR_HIGHLIGHTED_BLOCK_IDX}
                    title={highlightedBlocks[MAJOR_HIGHLIGHTED_BLOCK_IDX].title}
                    cardImage={highlightedBlocks[MAJOR_HIGHLIGHTED_BLOCK_IDX].cardImage}
                    path={highlightedBlocks[MAJOR_HIGHLIGHTED_BLOCK_IDX].referencedPath}
                    isTopProduct
                  />
                ) : (
                  <ProductCardDataLayerContainer
                    position={MAJOR_HIGHLIGHTED_BLOCK_IDX}
                    path={highlightedBlocks[MAJOR_HIGHLIGHTED_BLOCK_IDX].referencedPath}
                    title={highlightedBlocks[MAJOR_HIGHLIGHTED_BLOCK_IDX].title}
                    description={highlightedBlocks[MAJOR_HIGHLIGHTED_BLOCK_IDX].description}
                    cardImage={highlightedBlocks[MAJOR_HIGHLIGHTED_BLOCK_IDX].cardImage}
                    backgroundImage={highlightedBlocks[MAJOR_HIGHLIGHTED_BLOCK_IDX].backgroundImage}
                    showHand={highlightedBlocks[MAJOR_HIGHLIGHTED_BLOCK_IDX].showHand}
                    id={null}
                    cardType={ProductCardType.L}
                    isTopProduct
                  />
                )
              }
            </div>
          ) : null
        }

        {
          suggestions ? suggestions.map((product, idx) => (
            layoutExtension === LayoutExtension.Mobile ? (
              <TinyProductCardDataLayerContainer
                key={`search-suggestion_${idx}`}
                position={idx + ITEM_POSITION_SHIFT + numberOfHighlightedBlocks}
                title={product.title}
                cardImage={product.image}
                path={product.path}
                productType={product.productType}
                itemListID={ItemListID.SEARCH_SUGGESTIONS}
              />
            ) : (
              <ProductCardDataLayerContainer
                key={`search-suggestion_${idx}`}
                position={idx + ITEM_POSITION_SHIFT + numberOfHighlightedBlocks}
                title={product.title}
                description={product.description}
                path={product.path}
                cardImage={product.image}
                backgroundColor={product.backgroundColor}
                id={null}
                showHand={false}
                cardType={ProductCardType.S}
                productType={product.productType}
                showShadow={product.showShadow}
              />
            )
          )) : null
        }
      </div>
    </div>
  );
});

SearchSuggestions.displayName = 'SearchSuggestions';
